import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

// Images
// Images
import aboutDesktop from '../../../assets/images/public_profile_samples/Media-About-Desktop.png';
import hmuDesktop from '../../../assets/images/public_profile_samples/Media-HMU-Desktop.png';
import wardrobeDesktop from '../../../assets/images/public_profile_samples/Media-Wardrobe-Desktop.png';
import chatDesktop from '../../../assets/images/public_profile_samples/Media-Chat-Sample-Desktop.png';
import aboutMobile from '../../../assets/images/public_profile_samples/Media-About-Mobile.png';
import hmuMobile from '../../../assets/images/public_profile_samples/Media-HMU-Mobile.png';
import wardrobeMobile from '../../../assets/images/public_profile_samples/Media-Wardrobe-Mobile.png';
import chatMobile from '../../../assets/images/public_profile_samples/Media-Chat-Sample-Mobile.png';



interface PublicProfileSampleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const images = [
  { desktop: aboutDesktop, mobile: aboutMobile, label: 'Profile Bio & Skills' },
  { desktop: hmuDesktop, mobile: hmuMobile, label: 'Hair & Makeup Kit' },
  { desktop: wardrobeDesktop, mobile: wardrobeMobile, label: 'Wardrobe Kit' },
  { desktop: chatDesktop, mobile: chatMobile, label: 'Public Chat' },
];

const PublicProfileSampleModal: React.FC<PublicProfileSampleModalProps> = ({ isOpen, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  if (!isOpen) return null;

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-md transition-all duration-300 flex items-center justify-center p-4 mt-10 bg-gray-600 bg-opacity-40 mt-14 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-white flex flex-col items-center overflow-hidden animate-in fade-in zoom-in duration-300"
        style={{
          width: 'min(37.5rem, 100%)', // 600px
          height: 'min(49.1875rem, 90vh)', // 787px
          borderRadius: '0.5rem',
          padding: '1.875rem',
          gap: '1.25rem',
          opacity: 1,
          boxShadow: '0 0.625rem 2.5rem rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center w-full mb-2">
          <h2 className="text-xl font-bold text-gray-900 tracking-tight">How Public Profiles will be shown</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-900 transition-all p-1"
          >
            <X size={24} />
          </button>
        </div>

        {/* Carousel Container (Card Style) */}
        <div
          className="relative w-full flex-1 overflow-hidden rounded-xl border border-gray-300 bg-gray-100"
          style={{ aspectRatio: '4 / 5' }}
        >
          <div
            className="flex h-full transition-transform duration-700 cubic-bezier(0.4, 0, 0.2, 1)"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {images.map((img, idx) => (
              <div key={idx} className="min-w-full h-full flex items-center justify-center">
                <img
                  src={img.desktop}
                  alt={img.label}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Navigation & Indicators */}
        <div className="flex items-center justify-center gap-6 mt-2">
          <button
            type="button"
            onClick={prevSlide}
            className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-all"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>

          <div className="pagination flex gap-2 items-center">
            {images.map((_, idx) => (
              <div
                key={idx}
                className={`dot transition-all duration-300 ${currentIndex === idx ? 'active' : ''
                  }`}
                onClick={() => setCurrentIndex(idx)}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={nextSlide}
            className="w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-all"
          >
            <ChevronRight size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Footer Text */}
        <div className="text-center px-2">
          <p className="text-sm leading-5 text-gray-600 font-medium">
            Your link appears as a FILMIK profile card in text or email and opens a clean, view-only version of your profile. Choose which kits are visible on your public link, and toggle them on or off at any time.
          </p>
        </div>
      </div>

      <style>{`
        .pagination .dot {
          width: 0.5rem;
          height: 0.5rem;
          border-radius: 50%;
          background-color: #D1D5DB;
          cursor: pointer;
        }

        .pagination .dot.active {
          width: 1.5rem;
          height: 0.5rem;
          border-radius: 0.25rem;
          background-color: #3B82F6;
        }
      `}</style>
    </div>
  );
};

export default PublicProfileSampleModal;


